import {
  Experiment as browserExperimentSDK,
  ExperimentClient as BrowserExperimentClient,
} from '@amplitude/experiment-js-client';
import {
  Experiment as nodeExperimentSDK,
  RemoteEvaluationClient as NodeExperimentClient,
} from '@amplitude/experiment-node-server';
import { Plugin } from '@nuxt/types';
import { ExperimentKey, VariantValue } from '~/types/experiments';
import { isClientAmplitude } from '~/utils/amplitude';
import flagData from '~/data/flags';
interface AmplitudeExperimentPlugin {
  $amplitudeExperimentRefetchVariants: () => void;
  $getExperiment: <T extends ExperimentKey>(
    key: T
  ) => { value: VariantValue<T>; payload: any } | null;
  $getExperimentVariant: <T extends ExperimentKey>(key: T) => VariantValue<T> | undefined;
  $isControlExperiment: (key: ExperimentKey) => boolean;
  $isExperimentOfVariantType: (key: ExperimentKey, value: string) => boolean;
  $experimentPayload: (key: ExperimentKey) => any;
  $isFlagOn: (key: ExperimentKey) => boolean;
}
declare module '@nuxt/types' {
  interface Context extends AmplitudeExperimentPlugin {}
}

declare module 'vuex/types/index' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Store<S> extends AmplitudeExperimentPlugin {}
}

let client: null | BrowserExperimentClient | NodeExperimentClient = null;

const amplitudeExperimentPlugin: Plugin = async (
  { $auth, store, $amplitude, $cookies },
  inject
) => {
  const updateVariants = async (): Promise<void> => {};

  inject('amplitudeExperimentRefetchVariants', async (): Promise<void> => {
    await updateVariants();
  });

  inject(
    'getExperiment',
    <T extends ExperimentKey>(key: T): { value: VariantValue<T>; payload: any } | null => {
      return store.getters.experiments[key] ?? null;
    }
  );

  inject('getExperimentVariant', <T extends ExperimentKey>(key: T): VariantValue<T> | undefined => {
    const variant = store.getters.experiments[key];
    return variant?.value ?? undefined;
  });

  inject('isControlExperiment', (key: ExperimentKey): boolean => {
    if (!store.getters.experiments[key]) {
      return true;
    }
    return store.getters.experiments[key]?.value === 'control';
  });

  inject('isExperimentOfVariantType', (key: ExperimentKey, value: string): boolean => {
    const variant = store.getters.experiments[key]?.value;
    if (variant) {
      // Check variant exists in array
      if (Array.isArray(value)) {
        return value.includes((v: string) => v === variant);
      }
      return value === variant;
    }
    return false;
  });

  inject('experimentPayload', (key: ExperimentKey): any => {
    return store.getters.experiments[key]?.payload;
  });

  inject('isFlagOn', (key: string): boolean => {
    let flagValue = flagData[key];
    if (flagValue === undefined) {
      flagValue = false;
    }
    if (process.browser && key === 'recaptcha') {
      flagValue = !window.location.href.includes('https://staging-prexit.teachstarter.com/');
    }
    return flagValue;
  });

  /**
   * Experiment specific helpers
   */
  client = null;

  await updateVariants();
};

export default amplitudeExperimentPlugin;
